import i18next from 'i18next';
import cloneDeep from 'lodash.clonedeep';
import type { Category, EnhancedProperty } from '../../@types/item';
import { numberSeparator } from '../../helpers/formats';
import { getMarket } from '../../i18n';
import { getConvertedMileageValue } from '../market';

export const TEXT_TYPE = 'text';
export const NUMBER_TYPE = 'number';
export const BASIC_CATEGORY = Object.freeze({
  id: 1000,
  name: i18next.t('Basic information', { ns: 'item' }),
  subCategory: null,
});
export const ENGINE_SUBCATEGORY = Object.freeze({
  id: 6,
  name: i18next.t('Engine & Performance', { ns: 'item' }),
  subCategory: {
    id: 54,
    name: i18next.t('Engine', { ns: 'item' }),
    subCategory: null,
  },
});

class PropertyBuilder {
  name = '';
  value: any = '';
  type = 'unknown';
  unit: string | null = null;
  hint: string | null = null;
  category: Category | null = null;

  withName(name: string) {
    this.name = name;
    return this;
  }

  withValue(value: any) {
    this.value = value;
    return this;
  }

  withType(type: string) {
    this.type = type;
    return this;
  }

  withCategory(category: Category) {
    this.category = category;
    return this;
  }

  withUnit(unit: string) {
    this.unit = unit;
    return this;
  }

  build(): EnhancedProperty {
    return {
      name: this.name,
      value: this.value,
      type: this.type,
      unit: this.unit,
      hint: this.hint,
      category: this.category,
    };
  }
}

export default (item: any, properties: { [key: string]: EnhancedProperty }) => {
  const newProperties = cloneDeep(properties);

  if (!!item.manufacturer) {
    newProperties.manufacturer = new PropertyBuilder()
      .withName(i18next.t('manufacturer', { ns: 'glossary' }))
      .withValue(item.manufacturer)
      .withType(TEXT_TYPE)
      .withCategory(BASIC_CATEGORY)
      .build();
  }

  if (!!item.modelSeries) {
    newProperties.modelSeries = new PropertyBuilder()
      .withName(i18next.t('model', { ns: 'glossary' }))
      .withValue(item.modelSeries)
      .withType(TEXT_TYPE)
      .withCategory(BASIC_CATEGORY)
      .build();
  }

  if (!!item.modelName) {
    newProperties.modelName = new PropertyBuilder()
      .withName(i18next.t('version', { ns: 'glossary' }))
      .withValue(item.modelName)
      .withType(TEXT_TYPE)
      .withCategory(BASIC_CATEGORY)
      .build();
  }

  if (!!item.modelYear) {
    newProperties.modelYear = new PropertyBuilder()
      .withName(i18next.t('modelYear', { ns: 'glossary' }))
      .withValue(item.modelYear)
      .withType(NUMBER_TYPE)
      .withCategory(BASIC_CATEGORY)
      .build();
  }

  if (!!item.manufactureYear) {
    newProperties.manufactureYear = new PropertyBuilder()
      .withName(i18next.t('manufactureYear', { ns: 'glossary' }))
      .withValue(item.manufactureYear)
      .withType(NUMBER_TYPE)
      .withCategory(BASIC_CATEGORY)
      .build();
  }

  if (!!item.mileage) {
    const market = getMarket().marketCode;
    const mileageValue = item.odometerReading
      ? item.odometerReading.value
      : getConvertedMileageValue(item.mileage);
    let calculatedMilageValue = mileageValue;
    const milageUnit = item.odometerReading ? item.odometerReading.unit : 'km';

    if (market === 'NO' && milageUnit === 'mil') {
      calculatedMilageValue = mileageValue * 10;
    } else if (market === 'SE' && milageUnit === 'km') {
      calculatedMilageValue = mileageValue / 10;
    }

    newProperties.mileage = new PropertyBuilder()
      .withName(i18next.t('odometer', { ns: 'glossary' }))
      .withValue(numberSeparator(calculatedMilageValue))
      .withType(TEXT_TYPE)
      .withCategory(BASIC_CATEGORY)
      .withUnit(
        i18next
          .t('mileageUnit', {
            ns: 'glossary',
            count: calculatedMilageValue,
          })
          .toLowerCase(),
      )
      .build();
  }

  if (!!item.gearboxType) {
    newProperties.gearboxType = new PropertyBuilder()
      .withName(i18next.t('gearbox', { ns: 'glossary' }))
      .withValue(item.gearboxType)
      .withType(TEXT_TYPE)
      .withCategory(BASIC_CATEGORY)
      .build();
  }

  if (!!item.registrationNumber) {
    newProperties.registrationNumber = new PropertyBuilder()
      .withName(i18next.t('registrationNumber', { ns: 'glossary' }))
      .withValue(item.registrationNumber)
      .withType(TEXT_TYPE)
      .withCategory(BASIC_CATEGORY)
      .build();
  }

  if (!!item.vinNumber) {
    newProperties.vinNumber = new PropertyBuilder()
      .withName(i18next.t('vin', { ns: 'glossary' }))
      .withValue(item.vinNumber)
      .withType(TEXT_TYPE)
      .withCategory(BASIC_CATEGORY)
      .build();
  }

  if (item.enginePower) {
    newProperties.enginePower = new PropertyBuilder()
      .withName(i18next.t('enginePower', { ns: 'glossary' }))
      .withValue(item.enginePower)
      .withUnit(i18next.t('enginePowerUnit', { ns: 'glossary' }))
      .withType(NUMBER_TYPE)
      .withCategory(ENGINE_SUBCATEGORY)
      .build();
  }

  if (item?.properties?.numberOfDoors) {
    newProperties.numberOfDoors = new PropertyBuilder()
      .withName(i18next.t('numberOfDoors', { ns: 'glossary' }))
      .withValue(item.properties.numberOfDoors)
      .withType(NUMBER_TYPE)
      .withCategory(BASIC_CATEGORY)
      .build();
  }

  if (!!item.fuelTypes?.length) {
    newProperties.fuelTypes = new PropertyBuilder()
      .withName(i18next.t('fuelTypes', { ns: 'glossary' }))
      .withValue(
        Array.isArray(item.fuelTypes)
          ? (item?.fuelTypes as { name: string }[])?.map((x) => x.name)?.join(', ')
          : item.fuelTypes,
      )
      .withType(TEXT_TYPE)
      .withCategory(BASIC_CATEGORY)
      .build();
  }

  if (!!item.engineBaseType) {
    newProperties.engineBaseType = new PropertyBuilder()
      .withName(i18next.t('engineBaseType', { ns: 'glossary' }))
      .withValue(item.engineBaseType)
      .withType(TEXT_TYPE)
      .withCategory(BASIC_CATEGORY)
      .build();
  }

  return newProperties;
};
